import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiUrl, mediaUrl } from '../services/api';
import { Link, useParams } from 'react-router-dom';
import DescriptionDisplay from '../components/DescriptionDisplay';
import PageActions from '../components/PageActions';
import ActivityTypeSelect from '../components/ActivityTypeSelect';
import AddressSuggestInput from '../components/AddressSuggestInput';
import TravelRoutingEditor from '../components/TravelRoutingEditor';
import FlightStatus from '../components/FlightStatus';
import {
  buildLegsPayload,
  deriveStartEnd,
  hydrateLegDraftsFromActivity,
  cityKeyFromText,
  openNativePicker,
} from '../utils/travelLegs';
import { formatTime24, formatDateLocal } from '../utils/dateTime';
import { requireFields } from '../utils/validation';
import { buildIsTravelType } from '../utils/activityTypes';

// ---------------------------------------------------------------------------
// Inspiration linking (clips ↔ this itinerary activity)
// ---------------------------------------------------------------------------
function PlatformTag({ platform }) {
  if (!platform || platform === 'unknown') return null;
  const map = {
    tiktok: { bg: '#010101', label: 'TikTok' },
    youtube: { bg: '#ff0000', label: 'YouTube' },
    instagram: { bg: '#c13584', label: 'Instagram' },
  };
  const s = map[platform] || { bg: '#555', label: platform };
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '1px 6px',
      borderRadius: 4, background: s.bg, color: '#fff',
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {s.label}
    </span>
  );
}

function ClipThumb({ clip, size = 64 }) {
  // TikTok hands out signed CDN URLs that can expire, so fall back to ▶.
  const [failed, setFailed] = useState(false);
  const src = clip.thumbnail_url && !failed ? mediaUrl(clip.thumbnail_url) : null;
  return (
    <div style={{
      width: size, height: size, flexShrink: 0, borderRadius: 6, overflow: 'hidden',
      background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--tp-muted)',
    }}>
      {src
        ? <img src={src} alt="" onError={() => setFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <span style={{ fontSize: Math.round(size / 3) }}>▶</span>}
    </div>
  );
}

function InspirationSection({
  linkedClips, tripClips, showPicker, setShowPicker,
  clipUrl, setClipUrl, clipBusy, clipError, clipFilter, setClipFilter,
  onAddUrl, onLink, onUnlink,
}) {
  const linkedIds = new Set(linkedClips.map(c => c.clip_id));
  const q = clipFilter.trim().toLowerCase();
  const filtered = tripClips.filter(c =>
    !q
    || String(c.title || '').toLowerCase().includes(q)
    || String(c.author_name || '').toLowerCase().includes(q)
  );

  return (
    <div className="tp-col-12">
      <div className="tp-field">
        <span className="tp-label">Inspiration</span>

        {linkedClips.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {linkedClips.map(clip => (
              <div key={clip.clip_id} style={{
                display: 'flex', gap: 8, alignItems: 'center', padding: 8,
                border: '1px solid var(--tp-border)', borderRadius: 8, maxWidth: 300,
              }}>
                <a href={clip.url} target="_blank" rel="noreferrer" title="Open clip">
                  <ClipThumb clip={clip} />
                </a>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <PlatformTag platform={clip.platform} />
                  <div style={{
                    fontSize: 12, lineHeight: 1.3, overflow: 'hidden',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {clip.title || clip.url}
                  </div>
                  <button
                    type="button"
                    className="tp-btn--ghost"
                    style={{ alignSelf: 'flex-start', fontSize: 12, padding: '2px 8px' }}
                    onClick={() => onUnlink(clip.clip_id)}
                  >
                    Unlink
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--tp-muted)' }}>No inspiration linked yet.</div>
        )}

        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => setShowPicker(v => !v)}>
            {showPicker ? 'Done' : '+ Link inspiration'}
          </button>
        </div>

        {showPicker && (
          <div className="tp-card tp-card--tight" style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Paste a new link — saved to Inspiration and linked here in one step. */}
            <div>
              <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginBottom: 6 }}>Paste a TikTok or YouTube link</div>
              <div className="tp-row" style={{ gap: 8 }}>
                <input
                  type="url"
                  placeholder="https://www.tiktok.com/…  or  https://youtu.be/…"
                  value={clipUrl}
                  onChange={e => setClipUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAddUrl(); } }}
                  style={{ flex: 1 }}
                />
                <button type="button" onClick={onAddUrl} disabled={clipBusy || !clipUrl.trim()}>
                  {clipBusy ? 'Adding…' : 'Add'}
                </button>
              </div>
              {clipError && <div style={{ color: 'var(--tp-danger, #e05)', fontSize: 12, marginTop: 6 }}>{clipError}</div>}
              <div style={{ fontSize: 11, color: 'var(--tp-muted)', marginTop: 4 }}>
                New links are also saved to this trip’s Inspiration.
              </div>
            </div>

            {/* Or link a clip already saved to this trip. */}
            {tripClips.length > 0 && (
              <div>
                <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginBottom: 6 }}>Or link a saved clip</div>
                {tripClips.length > 6 && (
                  <input
                    type="text"
                    placeholder="Filter clips…"
                    value={clipFilter}
                    onChange={e => setClipFilter(e.target.value)}
                    style={{ width: '100%', marginBottom: 8 }}
                  />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
                  {filtered.map(clip => {
                    const linked = linkedIds.has(clip.clip_id);
                    return (
                      <div key={clip.clip_id} className="tp-row" style={{
                        justifyContent: 'space-between', alignItems: 'center', gap: 8,
                        padding: 6, borderRadius: 8, border: '1px solid var(--tp-border)',
                      }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                          <ClipThumb clip={clip} size={40} />
                          <div style={{ minWidth: 0 }}>
                            <PlatformTag platform={clip.platform} />
                            <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                              {clip.title || clip.url}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={linked ? 'tp-btn--ghost' : ''}
                          style={{ fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}
                          onClick={() => (linked ? onUnlink(clip.clip_id) : onLink(clip.clip_id))}
                        >
                          {linked ? 'Linked ✓' : 'Link'}
                        </button>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && (
                    <div style={{ color: 'var(--tp-muted)', fontSize: 12 }}>No matching clips.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Activity() {
  const { tripId, activityId } = useParams();

  const [tripName, setTripName] = useState('');
  const [destinations, setDestinations] = useState([]);

  const [activityTypes, setActivityTypes] = useState([]);
  const [activityCategories, setActivityCategories] = useState([]);

  const [activity, setActivity] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Inspiration linking (clips ↔ this activity)
  const [tripClips, setTripClips] = useState([]);
  const [showClipPicker, setShowClipPicker] = useState(false);
  const [clipUrl, setClipUrl] = useState('');
  const [clipBusy, setClipBusy] = useState(false);
  const [clipError, setClipError] = useState('');
  const [clipFilter, setClipFilter] = useState('');
  const [edit, setEdit] = useState({
    title: '',
    description: '',
    destination_id: '',
    category_id: '',
    type_id: '',
    date: '',
    start_time: '',
    end_time: '',
    location_address: '',
    location_lat: null,
    location_lon: null,
    location_city: '',
    airline: '',
    from_address: '',
    from_lat: null,
    from_lon: null,
    from_city: '',
    to_address: '',
    to_lat: null,
    to_lon: null,
    to_city: '',
    use_legs: false,
    legs: [],
  });

  useEffect(() => {
    fetch(apiUrl('/api/trips'))
      .then(res => res.json())
      .then(trips => {
        const trip = trips.find(t => String(t.trip_id) === String(tripId));
        if (trip) setTripName(trip.trip_name);
      })
      .catch(() => { /* ignore */ });

    fetch(apiUrl(`/api/trips/${tripId}/destinations`))
      .then(res => res.ok ? res.json() : [])
      .then(setDestinations)
      .catch(() => { /* ignore */ });

    fetch(apiUrl('/api/activity_types'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityTypes)
      .catch(() => { /* ignore */ });

    fetch(apiUrl('/api/activity_categories'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityCategories)
      .catch(() => { /* ignore */ });
  }, [tripId]);

  const refetchTypes = useCallback(() => {
    fetch(apiUrl('/api/activity_types'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityTypes)
      .catch(() => {});
  }, []);

  const loadActivity = () => {
    fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}`))
      .then(res => res.ok ? res.json() : null)
      .then(setActivity)
      .catch(() => setActivity(null));
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}/files`), {
        method: 'POST',
        body: form,
      });
      if (res.ok) loadActivity();
      else {
        const err = await res.json().catch(() => ({}));
        window.alert(err.error || 'Upload failed');
      }
    } catch {
      window.alert('Upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Remove this attachment?')) return;
    const res = await fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}/files/${fileId}`), { method: 'DELETE' });
    if (res.ok) loadActivity();
  };

  const loadTripClips = () => {
    fetch(apiUrl(`/api/trips/${tripId}/clips`))
      .then(res => res.ok ? res.json() : [])
      .then(data => setTripClips(Array.isArray(data) ? data : []))
      .catch(() => setTripClips([]));
  };

  // Only TikTok/YouTube are recognized today (Instagram is deferred), so guard
  // before saving — matches the Inspiration page, which rejects unknown links.
  const isSupportedClipUrl = (u) => /tiktok\.com|youtube\.com|youtu\.be/i.test(u);

  const linkClip = async (clipId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/activities/${activityId}`), { method: 'POST' });
    loadActivity();
  };

  const unlinkClip = async (clipId) => {
    await fetch(apiUrl(`/api/clips/${clipId}/activities/${activityId}`), { method: 'DELETE' });
    loadActivity();
  };

  const addClipByUrl = async () => {
    const url = clipUrl.trim();
    if (!url) return;
    if (!isSupportedClipUrl(url)) {
      setClipError('Paste a TikTok or YouTube link.');
      return;
    }
    setClipBusy(true);
    setClipError('');
    try {
      const res = await fetch(apiUrl(`/api/trips/${tripId}/clips`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url,
          activity_ids: [activityId],
          destination_ids: activity?.destination_id ? [activity.destination_id] : [],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setClipError(err.error || 'Could not add that link.');
        return;
      }
      setClipUrl('');
      loadActivity();
      loadTripClips();
    } catch {
      setClipError('Could not add that link.');
    } finally {
      setClipBusy(false);
    }
  };

  useEffect(() => {
    loadActivity();
    loadTripClips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, activityId]);

  const resolvedDestinationName = useMemo(() => {
    if (!activity) return '';
    const d = destinations.find(x => String(x.destination_id) === String(activity.destination_id));
    return d ? d.destination_name : '';
  }, [activity, destinations]);

  const destinationTimeZone = useMemo(() => {
    if (!activity) return 'UTC';
    const d = destinations.find(x => String(x.destination_id) === String(activity.destination_id));
    return (d && d.timezone) ? d.timezone : 'UTC';
  }, [activity, destinations]);

  const resolvedType = useMemo(() => {
    if (!activity || !activity.type_id) return null;
    return activityTypes.find(t => String(t.type_id) === String(activity.type_id)) || null;
  }, [activity, activityTypes]);

  const resolvedCategoryName = useMemo(() => {
    if (!resolvedType) return '';
    const c = activityCategories.find(x => String(x.category_id) === String(resolvedType.category_id));
    return c ? c.category_name : '';
  }, [resolvedType, activityCategories]);

  const isTravelTypeId = useMemo(
    () => buildIsTravelType(activityTypes, activityCategories),
    [activityTypes, activityCategories]
  );

  const startEditing = () => {
    if (!activity) return;
    const type = activity.type_id
      ? (activityTypes.find(t => String(t.type_id) === String(activity.type_id)) || null)
      : null;
    const categoryId = type ? String(type.category_id) : '';

    const date = formatDateLocal(activity.start_time, destinationTimeZone) || formatDateLocal(activity.end_time, destinationTimeZone) || '';
    const startTime = formatTime24(activity.start_time, destinationTimeZone);
    const endTime = formatTime24(activity.end_time, destinationTimeZone);

    const legsDraft = hydrateLegDraftsFromActivity(activity, date);

    const useLegs = legsDraft.length > 0;
    const ensuredLegs = useLegs
      ? legsDraft
      : [{
        leg_index: 0,
        type_id: activity.type_id ? String(activity.type_id) : '',
        airline: activity.airline || '',
        from_address: activity.from_address || '',
        from_lat: activity.from_lat ?? null,
        from_lon: activity.from_lon ?? null,
        from_city: activity.from_city || '',
        to_address: activity.to_address || '',
        to_lat: activity.to_lat ?? null,
        to_lon: activity.to_lon ?? null,
        to_city: activity.to_city || '',
        depart_date: date,
        depart_time: startTime || '',
        arrive_day_offset: 0,
        arrive_time: endTime || '',
      }];

    setEdit({
      title: activity.title || '',
      description: activity.description || '',
      destination_id: activity.destination_id ? String(activity.destination_id) : '',
      category_id: categoryId,
      type_id: activity.type_id ? String(activity.type_id) : '',
      date,
      start_time: startTime,
      end_time: endTime,
      location_address: activity.location_address || '',
      location_lat: activity.location_lat ?? null,
      location_lon: activity.location_lon ?? null,
      location_city: activity.location_city || '',
      booking_url: activity.booking_url || '',
      booking_ref: activity.booking_ref || '',
      airline: activity.airline || '',
      from_address: activity.from_address || '',
      from_lat: activity.from_lat ?? null,
      from_lon: activity.from_lon ?? null,
      from_city: activity.from_city || '',
      to_address: activity.to_address || '',
      to_lat: activity.to_lat ?? null,
      to_lon: activity.to_lon ?? null,
      to_city: activity.to_city || '',
      use_legs: useLegs,
      legs: ensuredLegs,
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const save = async () => {
    const title = String(edit.title || '').trim();
    if (!requireFields([[title, 'Title']])) return;

    const useLegs = !!edit.use_legs;
    const editLegs = Array.isArray(edit.legs) ? edit.legs : [];

    const legsPayload = buildLegsPayload({
      useLegs,
      legsDraft: editLegs,
      typeId: edit.type_id,
      fallbackDate: edit.date,
    });

    if (useLegs && (!legsPayload || !legsPayload.length)) {
      window.alert('Please add at least one leg with From/To.');
      return;
    }

    const date = edit.date || null;
    const { startIso, endIso } = deriveStartEnd({
      useLegs,
      legsPayload,
      dateStr: date,
      startTime: edit.start_time,
      endTime: edit.end_time,
    });

    const from_address = String(edit.from_address || '').trim();
    const to_address = String(edit.to_address || '').trim();
    const location_address = String(edit.location_address || '').trim();
    const location_lat = Number.isFinite(Number(edit.location_lat)) ? Number(edit.location_lat) : null;
    const location_lon = Number.isFinite(Number(edit.location_lon)) ? Number(edit.location_lon) : null;
    const location_city = String(edit.location_city || '').trim() || (location_address ? cityKeyFromText(location_address) : null);
    const booking_url = String(edit.booking_url || '').trim();
    const booking_ref = String(edit.booking_ref || '').trim();
    const existingRoute = !!(activity?.from_address || activity?.to_address || activity?.from_lat || activity?.to_lat);
    const isTravel = isTravelTypeId(edit.type_id || activity?.type_id);
    const includeRoute = (!useLegs) && (isTravel || existingRoute || from_address || to_address);

    const res = await fetch(apiUrl(`/api/trips/${tripId}/activities/${activityId}`),
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          description: edit.description || '',
          destination_id: edit.destination_id || null,
          type_id: edit.type_id || null,
          location_address: location_address || null,
          location_lat,
          location_lon,
          location_city: location_city || null,
          booking_url: booking_url || null,
          booking_ref: booking_ref || null,
          start_time: startIso,
          end_time: endIso,
          ...(useLegs ? { legs: legsPayload } : {}),
          ...(includeRoute ? {
            airline: String(edit.airline || '').trim() || null,
            from_address: from_address || null,
            from_lat: Number.isFinite(Number(edit.from_lat)) ? Number(edit.from_lat) : null,
            from_lon: Number.isFinite(Number(edit.from_lon)) ? Number(edit.from_lon) : null,
            from_city: String(edit.from_city || '').trim() || null,
            to_address: to_address || null,
            to_lat: Number.isFinite(Number(edit.to_lat)) ? Number(edit.to_lat) : null,
            to_lon: Number.isFinite(Number(edit.to_lon)) ? Number(edit.to_lon) : null,
            to_city: String(edit.to_city || '').trim() || null,
          } : {}),
        })
      }
    );

    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(payload.error || 'Failed to update activity');
      return;
    }

    setIsEditing(false);
    loadActivity();
  };

  return (
    <div>
      <section className="tp-hero">
        <p className="tp-hero__kicker">Activity</p>
        <h1 className="tp-hero__title">{activity?.title || 'Activity'}</h1>
        <p className="tp-hero__subtitle">{tripName ? `Trip: ${tripName}` : ''}</p>
        <PageActions
          actions={[
            { label: 'Back to Itinerary', to: `/itinerary/${tripId}`, variant: 'ghost' },
            { label: 'Back to Home', to: '/', variant: 'ghost' },
          ]}
        />
      </section>

      {!activity && (
        <section className="tp-section">
          <div className="tp-card">
            <p style={{ margin: 0, color: 'var(--tp-muted)' }}>Activity not found.</p>
          </div>
        </section>
      )}

      {activity && (
        <section className="tp-section">
          <div className="tp-card">
            <div className="tp-row" style={{ justifyContent: 'space-between' }}>
              <h2 className="tp-section__title" style={{ margin: 0 }}>Details</h2>
              {isEditing ? (
                <div className="tp-row">
                  <button onClick={save}>Save</button>
                  <button className="tp-btn--ghost" onClick={cancelEditing}>Cancel</button>
                </div>
              ) : (
                <button onClick={startEditing}>Edit</button>
              )}
            </div>

            <div className="tp-grid" style={{ marginTop: 12, gap: 12 }}>
              <div className="tp-col-6">
                <div className="tp-field">
                  <span className="tp-label">Title</span>
                  {isEditing ? (
                    <input type="text" value={edit.title} onChange={e => setEdit(prev => ({ ...prev, title: e.target.value }))} />
                  ) : (
                    <div>{activity.title}</div>
                  )}
                </div>
              </div>

              <div className="tp-col-6">
                <div className="tp-field">
                  <span className="tp-label">Destination</span>
                  {isEditing ? (
                    <select
                      value={edit.destination_id || ''}
                      onChange={e => setEdit(prev => ({ ...prev, destination_id: e.target.value }))}
                    >
                      <option value="">(None)</option>
                      {destinations.map(d => (
                        <option key={d.destination_id} value={String(d.destination_id)}>{d.destination_name}</option>
                      ))}
                    </select>
                  ) : (
                    <div>{resolvedDestinationName || '—'}</div>
                  )}
                </div>
              </div>

              {(isTravelTypeId((isEditing ? edit.type_id : null) || activity?.type_id) || (Array.isArray(activity?.legs) && activity.legs.length) || activity?.from_address || activity?.to_address || (isEditing && (edit.from_address || edit.to_address))) && (
                <>
                  {isEditing ? (
                    <div className="tp-col-12">
                      <TravelRoutingEditor
                        draft={edit}
                        setDraft={setEdit}
                        activityTypes={activityTypes}
                        categoryId={edit.category_id}
                        fallbackDate={edit.date}
                        onTypeCreated={refetchTypes}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="tp-col-6">
                        <div className="tp-field">
                          <span className="tp-label">From</span>
                          <div>{(() => {
                            const legs = Array.isArray(activity?.legs) ? [...activity.legs] : [];
                            legs.sort((a, b) => (Number(a?.leg_index) || 0) - (Number(b?.leg_index) || 0));
                            if (legs.length) return legs[0]?.from_address || legs[0]?.from_city || '—';
                            return activity.from_address || '—';
                          })()}</div>
                        </div>
                      </div>
                      <div className="tp-col-6">
                        <div className="tp-field">
                          <span className="tp-label">To</span>
                          <div>{(() => {
                            const legs = Array.isArray(activity?.legs) ? [...activity.legs] : [];
                            legs.sort((a, b) => (Number(a?.leg_index) || 0) - (Number(b?.leg_index) || 0));
                            if (legs.length) {
                              const last = legs[legs.length - 1];
                              return last?.to_address || last?.to_city || '—';
                            }
                            return activity.to_address || '—';
                          })()}</div>
                        </div>
                      </div>
                      {(Array.isArray(activity?.legs) ? activity.legs : [])
                        .filter(l => l && l.flight_number)
                        .map((l, i) => (
                          <div className="tp-col-12" key={l.leg_id || i}>
                            <FlightStatus
                              flightNumber={l.flight_number}
                              date={String(l.depart_time || '').slice(0, 10)}
                              label={`${l.airline ? l.airline + ' ' : ''}${l.flight_number} · ${l.from_city || l.from_address || ''} → ${l.to_city || l.to_address || ''}`.trim()}
                            />
                          </div>
                        ))}
                    </>
                  )}
                </>
              )}

              <div className="tp-col-12">
                <div className="tp-field">
                  <span className="tp-label">Description</span>
                  {isEditing ? (
                    <textarea
                      rows={4}
                      placeholder="Description (Markdown supported)"
                      value={edit.description || ''}
                      onChange={e => setEdit(prev => ({ ...prev, description: e.target.value }))}
                      style={{ resize: 'vertical', minHeight: 80 }}
                    />
                  ) : (
                    <DescriptionDisplay text={activity.description} emptyFallback="—" />
                  )}
                </div>
              </div>

              <div className="tp-col-12">
                <div className="tp-field">
                  <span className="tp-label">Location (optional)</span>
                  {isEditing ? (
                    <AddressSuggestInput
                      value={edit.location_address || ''}
                      onChange={v => setEdit(prev => ({
                        ...prev,
                        location_address: v,
                        location_lat: null,
                        location_lon: null,
                        location_city: '',
                      }))}
                      onSelect={s => setEdit(prev => ({
                        ...prev,
                        location_address: s.display_name || s.label || '',
                        location_lat: Number(s.lat) || null,
                        location_lon: Number(s.lon) || null,
                        location_city: cityKeyFromText(s.label || s.display_name) || '',
                      }))}
                      placeholder="Add a location for this activity"
                    />
                  ) : (
                    <div>{activity.location_address || '—'}</div>
                  )}
                </div>
              </div>

              <div className="tp-col-12">
                <div className="tp-field">
                  <span className="tp-label">Booking link / tickets (optional)</span>
                  {isEditing ? (
                    <>
                      <input
                        type="url"
                        value={edit.booking_url || ''}
                        onChange={e => setEdit(prev => ({ ...prev, booking_url: e.target.value }))}
                        placeholder="https://… (GetYourGuide, Viator, ticket, etc.)"
                      />
                      <input
                        type="text"
                        value={edit.booking_ref || ''}
                        onChange={e => setEdit(prev => ({ ...prev, booking_ref: e.target.value }))}
                        placeholder="Confirmation / voucher number"
                        style={{ marginTop: 8 }}
                      />
                    </>
                  ) : (activity.booking_url || activity.booking_ref) ? (
                    <div>
                      {activity.booking_url ? (
                        <a href={activity.booking_url} target="_blank" rel="noreferrer">Open booking ↗</a>
                      ) : null}
                      {activity.booking_ref ? (
                        <div style={{ color: 'var(--tp-muted)', marginTop: 2 }}>Ref: {activity.booking_ref}</div>
                      ) : null}
                    </div>
                  ) : (
                    <div>—</div>
                  )}
                </div>
              </div>

              <div className="tp-col-12">
                <div className="tp-field">
                  <span className="tp-label">Tickets &amp; vouchers (PDF / image)</span>
                  {(activity.files || []).length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(activity.files || []).map(f => (
                        <div key={f.file_id} className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          <a href={f.media_url} target="_blank" rel="noreferrer">
                            {f.content_type === 'application/pdf' ? '📄' : '🖼'} {f.original_name || 'Attachment'} ↗
                          </a>
                          <button type="button" className="tp-btn--ghost" onClick={() => handleDeleteFile(f.file_id)}>Remove</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--tp-muted)' }}>No tickets attached yet.</div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    onChange={handleUploadFile}
                    style={{ marginTop: 8 }}
                  />
                </div>
              </div>

              <InspirationSection
                linkedClips={Array.isArray(activity.clips) ? activity.clips : []}
                tripClips={tripClips}
                showPicker={showClipPicker}
                setShowPicker={setShowClipPicker}
                clipUrl={clipUrl}
                setClipUrl={setClipUrl}
                clipBusy={clipBusy}
                clipError={clipError}
                clipFilter={clipFilter}
                setClipFilter={setClipFilter}
                onAddUrl={addClipByUrl}
                onLink={linkClip}
                onUnlink={unlinkClip}
              />

              <div className="tp-col-12">
                {isEditing ? (
                  <ActivityTypeSelect
                    categories={activityCategories}
                    types={activityTypes}
                    categoryId={edit.category_id}
                    typeId={edit.type_id}
                    onCategoryChange={v => setEdit(prev => ({ ...prev, category_id: v, type_id: '' }))}
                    onTypeChange={v => setEdit(prev => ({ ...prev, type_id: v }))}
                    onTypeCreated={newType => {
                      refetchTypes();
                      setEdit(prev => ({ ...prev, type_id: newType.type_id }));
                    }}
                    placeholderCategory="(None)"
                    placeholderType="(None)"
                  />
                ) : (
                  <div className="tp-row" style={{ gap: 24 }}>
                    <div>
                      <span className="tp-label">Category</span>
                      <div>{resolvedCategoryName || '—'}</div>
                    </div>
                    <div>
                      <span className="tp-label">Type</span>
                      <div>{resolvedType?.type_name || '—'}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="tp-col-4">
                <div className="tp-field">
                  <span className="tp-label">Date</span>
                  {isEditing ? (
                    <input type="date" value={edit.date || ''} onChange={e => setEdit(prev => ({ ...prev, date: e.target.value }))} />
                  ) : (
                    <div>{formatDateLocal(activity.start_time, destinationTimeZone) || formatDateLocal(activity.end_time, destinationTimeZone) || '—'}</div>
                  )}
                </div>
              </div>

              <div className="tp-col-4">
                <div className="tp-field">
                  <span className="tp-label">Start</span>
                  {isEditing ? (
                    <input type="time" step="60" value={edit.start_time || ''} onClick={openNativePicker} onChange={e => setEdit(prev => ({ ...prev, start_time: e.target.value }))} />
                  ) : (
                    <div>{formatTime24(activity.start_time, destinationTimeZone) || '—'}</div>
                  )}
                </div>
              </div>

              <div className="tp-col-4">
                <div className="tp-field">
                  <span className="tp-label">End</span>
                  {isEditing ? (
                    <input type="time" step="60" value={edit.end_time || ''} onClick={openNativePicker} onChange={e => setEdit(prev => ({ ...prev, end_time: e.target.value }))} />
                  ) : (
                    <div>{formatTime24(activity.end_time, destinationTimeZone) || '—'}</div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12, color: 'var(--tp-muted)', fontSize: 13 }}>
              <div>Activity ID: <code>{activity.activity_id}</code></div>
              <div style={{ marginTop: 6 }}>
                <Link to={`/itinerary/${tripId}`} style={{ color: 'inherit' }}>Back to itinerary</Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default Activity;
